/**
 * IMPORTS
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Lock, MessagesSquare, Search, UserX } from "lucide-react";

import { AcoesConvite } from "@components/notificacoes/acoes-convite";
import { NotificacoesSino } from "@components/notificacoes/notificacoes-sino";

import { useMinhasMensagens } from "@/hooks/conversa-cliente/use-minhas-mensagens";
import { useEstadoConversa } from "@/hooks/conversa-cliente/use-participantes-conversa";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { handleBaixarAnexo, handleBuscarImagemAnexo } from "@/domain/use-cases/conversa-cliente";
import type { IAnexoMensagem, IMensagemConversa } from "@/interface/conversa-cliente";
import { getApiErrorMessage } from "@/utils/normalize-api-error/normalize-api-error";
import { notifyError } from "@/utils/notify-messages";

import { CompositorConversa } from "./components/compositor-conversa";
import { MensagemConversa } from "./components/mensagem-conversa";
import { PainelParticipantes } from "./components/painel-participantes";

const ATRASO_BUSCA_MS = 400;

/**
 * Conversa do cliente com a equipe Letscom: todo o histórico da empresa + responder com texto e
 * anexos. Só entra quem a equipe chamou ao iniciar a conversa ou quem foi convidado e aceitou.
 */
function ConversaClientePage() {
  const accessToken = useStoreZustandUserAuth((state) => state.token);
  const [termo, setTermo] = useState("");
  const [busca, setBusca] = useState("");

  useEffect(() => {
    const temporizador = setTimeout(() => setBusca(termo.trim()), ATRASO_BUSCA_MS);
    return () => clearTimeout(temporizador);
  }, [termo]);

  const estado = useEstadoConversa();
  // Acesso inativado pelo dono: nada de lista/compositor, mesmo com mensagens ainda em cache.
  const acessoInativado = estado.data?.minha_participacao?.status === "inativo";
  const temAcesso = Boolean(estado.data?.tem_acesso) && !acessoInativado;
  const podeConversar = temAcesso && Boolean(estado.data?.iniciada);

  const {
    mensagens,
    totalMensagens,
    carregando,
    erro,
    recarregar,
    temAnteriores,
    carregarAnteriores,
    carregandoAnteriores,
    responder,
  } = useMinhasMensagens(busca || undefined, podeConversar);

  // Rola até a mais recente ao abrir/filtrar e quando chega mensagem nova;
  // ao carregar anteriores, mantém a posição de leitura.
  const listaRef = useRef<HTMLDivElement>(null);
  const ultimaIdRef = useRef<number | null>(null);
  const alturaAntesRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const lista = listaRef.current;
    if (!lista) return;

    if (alturaAntesRef.current !== null) {
      lista.scrollTop += lista.scrollHeight - alturaAntesRef.current;
      alturaAntesRef.current = null;
      return;
    }

    const ultimaId = mensagens.at(-1)?.id ?? null;
    if (ultimaId !== ultimaIdRef.current) {
      lista.scrollTop = lista.scrollHeight;
      ultimaIdRef.current = ultimaId;
    }
  }, [mensagens]);

  const handleCarregarAnteriores = () => {
    alturaAntesRef.current = listaRef.current?.scrollHeight ?? null;
    void carregarAnteriores();
  };

  const baixarAnexo = (mensagem: IMensagemConversa, anexo: IAnexoMensagem) => {
    if (!accessToken) return;
    handleBaixarAnexo(accessToken, mensagem.id, anexo).catch((falha: unknown) =>
      notifyError(getApiErrorMessage(falha))
    );
  };

  const carregarImagem = (mensagem: IMensagemConversa, anexo: IAnexoMensagem) =>
    accessToken
      ? handleBuscarImagemAnexo(accessToken, mensagem.id, anexo.id)
      : Promise.reject(new Error("Sessão expirada."));

  return (
    <div className="flex h-[calc(100dvh-5rem)] flex-col md:h-[calc(100dvh-2rem)]">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1 p-4 pb-2">
          <h2 className="text-2xl font-medium text-black">Conversa com a equipe</h2>
          <p className="text-sm text-gray-500">
            Acompanhe os registros da equipe Letscom e responda por aqui.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4">
          {podeConversar && estado.data && <PainelParticipantes estado={estado.data} />}
          <div className={`relative ${podeConversar ? "" : "hidden"}`}>
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={termo}
              onChange={(evento) => setTermo(evento.target.value)}
              placeholder="Buscar na conversa..."
              aria-label="Buscar na conversa"
              className="h-10 w-56 rounded-md border border-gray-300 bg-white pl-8 pr-3 text-sm text-gray-900 caret-gray-900 outline-none placeholder:text-gray-400 focus-visible:ring-2 focus-visible:ring-gray-custom900"
            />
          </div>
          <NotificacoesSino />
        </div>
      </div>

      <section
        aria-label="Conversa"
        className="mx-4 mb-2 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm"
      >
        <div ref={listaRef} className="flex-1 space-y-5 overflow-y-auto p-4">
          {podeConversar && temAnteriores && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleCarregarAnteriores}
                disabled={carregandoAnteriores}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {carregandoAnteriores ? "Carregando..." : "Carregar anteriores"}
              </button>
            </div>
          )}

          {estado.isLoading || (podeConversar && carregando) ? (
            <div className="space-y-5">
              {[0, 1, 2].map((indice) => (
                <div key={indice} className="flex gap-3">
                  <div className="size-8 animate-pulse rounded-full bg-gray-200" />
                  <div className="h-20 w-2/3 animate-pulse rounded-2xl bg-gray-200" />
                </div>
              ))}
            </div>
          ) : estado.isError || (podeConversar && erro) ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
              <p className="text-sm text-gray-500">Não foi possível carregar a conversa.</p>
              <button
                type="button"
                onClick={() => (estado.isError ? estado.refetch() : recarregar())}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
              >
                Tentar novamente
              </button>
            </div>
          ) : !temAcesso ? (
            <SemAcessoConversa
              inativo={acessoInativado}
              conviteId={
                estado.data?.minha_participacao?.status === "pendente"
                  ? estado.data.minha_participacao.id
                  : null
              }
            />
          ) : !podeConversar ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-gray-500">
              <MessagesSquare className="size-8" />
              <p className="text-sm">
                A equipe Letscom ainda não iniciou a conversa com você. Assim que ela enviar a
                primeira mensagem, você poderá responder por aqui.
              </p>
            </div>
          ) : mensagens.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-gray-500">
              <MessagesSquare className="size-8" />
              <p className="text-sm">
                {busca ? "Nenhuma mensagem encontrada." : "Ainda não há mensagens."}
              </p>
            </div>
          ) : (
            mensagens.map((mensagem) => (
              <MensagemConversa
                key={mensagem.id}
                mensagem={mensagem}
                onBaixarAnexo={(anexo) => baixarAnexo(mensagem, anexo)}
                carregarImagem={(anexo) => carregarImagem(mensagem, anexo)}
              />
            ))
          )}
        </div>

        {podeConversar && !carregando && totalMensagens > 0 && (
          <p className="border-t border-gray-200 px-4 py-1.5 text-right text-xs text-gray-500">
            {mensagens.length} de {totalMensagens} mensagens
          </p>
        )}

        {podeConversar && (
          <CompositorConversa
            enviando={responder.isPending}
            onEnviar={(payload) =>
              responder.mutateAsync(payload).catch((falha: unknown) => {
                notifyError(getApiErrorMessage(falha));
                throw falha;
              })
            }
          />
        )}
      </section>
    </div>
  );
}

/**
 * Fora da conversa: acesso inativado pelo dono → aviso; com convite pendente, aceita/recusa aqui
 * mesmo; sem convite, pede um.
 */
function SemAcessoConversa({ conviteId, inativo }: { conviteId: number | null; inativo: boolean }) {
  if (inativo) {
    return (
      <div
        role="status"
        className="flex h-full flex-col items-center justify-center gap-3 text-center text-gray-600"
      >
        <UserX className="size-8 text-gray-500" />
        <p className="text-base font-medium text-gray-900">
          Seu acesso a esta conversa foi desativado
        </p>
        <p className="max-w-sm text-sm text-gray-500">
          Quem iniciou a conversa desativou sua participação. Você não pode ler nem enviar mensagens
          por enquanto. Se precisar voltar, peça a essa pessoa para reativar seu acesso.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-gray-500">
      <Lock className="size-8" />
      {conviteId !== null ? (
        <>
          <p className="text-sm">Você foi convidado para participar desta conversa.</p>
          <AcoesConvite conviteId={conviteId} status="pendente" />
        </>
      ) : (
        <p className="max-w-sm text-sm">
          Você não participa desta conversa. Peça um convite a quem iniciou a conversa com a equipe
          Letscom.
        </p>
      )}
    </div>
  );
}

/**
 * EXPORTS
 */
export { ConversaClientePage };
